import type { Indexed, Option } from "types";
import { OpenBankingResultStatus } from "consts/transactions";
import { ApiState } from "infrastructure/api";
import type { RootState } from "infrastructure/store";
import { capitalizeFirstLetterFormatter, removeUnderscoreFormatter } from "util/formaters";
import { compareIds } from "util/support";
import { convertToOptions } from "util/transformers";
import type { BitsoBank, Country } from "./types";

export const selectMenuOpenState = (state: RootState) =>
  state.general.isSideMenuOpen;

export const selectNotification = (state: RootState) =>
  state.general.notifications;

export const selectConstants = (state: RootState) =>
  state.general.constants;

export const selectIsConstantsLoading = (state: RootState) =>
  state.general.constantsLoadingState === ApiState.Pending;

export const selectIsConstantsLoadingSucceeded = (state: RootState) =>
  state.general.constantsLoadingState === ApiState.Succeeded;

export const selectCountries = (sortFn = (countryA: Country, countryB: Country) => (
  countryA.shortName.localeCompare(countryB.shortName)
)) => (state: RootState) =>
    [state.general.countries]
      .flat()
      .filter(Boolean)
      .sort(sortFn);

export const selectCountriesByProperty = <TProperty extends keyof Country>(
  propertyName: TProperty,
  values: Array<Country[TProperty]>
) => (state: RootState) =>
    selectCountries()(state)
      .filter((country: Country) => values.includes(country[propertyName]));

export const selectCurrencies = (state: RootState) =>
  selectConstants(state).Currencies;

export const selectIsBitsoBanksLoading = (state: RootState) =>
  state.general.bitsoBanksLoadingState === ApiState.Pending;

export const selectBitsoBanks = (state: RootState) =>
  state.general.bitsoBanks;

export const selectBitsoBanksOptions = (state: RootState): Array<Option> =>
  selectBitsoBanks(state)?.data.map(({
    bitsoBankCode,
    bitsoBankName
  }: BitsoBank) => ({
    id: bitsoBankCode,
    name: bitsoBankName
  })) ?? [];

export const selectSetupActiveCompany = (state: RootState) =>
  Object.values<string>(selectConstants(state).SetupActiveCompany || {});

export const selectSetupActiveCompanyOptions = (state: RootState) =>
  convertToOptions(
    selectConstants(state).SetupActiveCompany
  )
  export const selectSetupGroupNames = (state: RootState) =>
    Object.values<string>(selectConstants(state).SetupGroups || {});

  export const selectSetupGroupNamesOptions = (state: RootState) =>
    convertToOptions(
      selectConstants(state).SetupGroups
    );

export const selectTransactionCurrencies = (state: RootState) =>
  selectConstants(state).TransactionCurrencies;

export const selectCryptoCurrencies = (state: RootState) =>
  selectConstants(state).CryptoCurrencies;

export const selectCryptoChains = (state: RootState) =>
  selectConstants(state).CryptoChains;

export const selectMerchantMethodPivotActiveDomain = (state: RootState) =>
  selectConstants(state).MerchantMethodPivotActiveDomainOption;

export const selectCountry = (keyValue: Partial<Indexed<Country>>, defaultValue = null) =>
  (state: RootState) => {
    const [key, value] = Object.entries(keyValue).at(0)!;

    const countryProperty = key as keyof Country;

    return state.general.countries.find((country: Country) =>
      compareIds(country[countryProperty], value)) ?? defaultValue;
  };

export const selectCountriesOptions = <
  TId extends keyof Country,
  TName extends keyof Country
>(option: Readonly<{
  id: TId;
  name?: TName;
}>, transformer?: (country: Indexed<Country>) => string | Array<Option>) =>
  (state: RootState) => {
    const callbackfn = transformer ?? ((country: Indexed<Country>) => ({
      id: country[option.id],
      name: country[option.name ?? option.id]
    }));

    return selectCountries()(state)
      .map(country => callbackfn(country));
  };

export const selectIsCountriesLoading = (state: RootState) =>
  state.general.countriesLoadingState === ApiState.Pending;

export const selectPSPs = (state: RootState) =>
  selectConstants(state).PSPs;

export const selectMerchantPayoutIncrementsViaOptions = (state: RootState) =>
  convertToOptions(
    selectConstants(state).MerchantPayoutIncrementsVia,
    capitalizeFirstLetterFormatter
  )

export const selectViaAutoSuggestModel = (state: RootState) =>
  selectConstants(state).Via.map(({ name }: Option) => ({
    id: name
      .charAt(0)
      .toLocaleLowerCase(),
    name
  }));

export const selectCalulationStatus = (state: RootState) =>
  selectConstants(state).MerchantPayoutCalculationEnums.map((calculationStatus: string) => ({
    id: calculationStatus,
    name: removeUnderscoreFormatter(calculationStatus)
  }));

export const selectFileTaxName = (state: RootState) =>
  selectConstants(state).MerchantPayoutFileTaxName;

export const selectParentVisibility = (state: RootState) =>
  state.general.extras.parentVisibility;

export const selectObResultStatusText = (obResultStatus: OpenBankingResultStatus) =>
  (state: RootState) =>
    selectConstants(state).ObResultStatuses.find(({ id }: Option) =>
      compareIds(id, obResultStatus))?.name ?? '';

export const selectCountryByProperty = ([propertyName, value]: [keyof Country, string]) =>
  (state: RootState) =>
    state.general.countries.find((country: Country) =>
      compareIds(country[propertyName], value));

export const selectUserPreferences = (state: RootState) =>
  state.general.userPreferences;

export const selectUserPreferencesLoadingState = (state: RootState) =>
  state.general.userPreferencesLoadingState;

export const selectKycTypesMap = (state: RootState) =>
  selectConstants(state).KycTypesMap;

export const selectMerchantGroupNames = (state: RootState) =>
  selectConstants(state).MerchantGroupNames;

export const selectPayoutCycles = (state: RootState) =>
  selectConstants(state).PayoutCycles;

export const selectAccountWithdrawalOptions = (state: RootState) =>
  selectConstants(state).AccountWithdrawalOptions;

export const selectMerchantPayoutIncrementExchangeRateActiveDomainOptions = (state: RootState) =>
  selectConstants(state).MerchantPayoutIncrementExchangeRateActiveDomainOption;

export const selectPaymentProducts = (state: RootState) =>
  selectConstants(state).PaymentProducts;

export const selectConnectorsFlowsOptions = (state: RootState) =>
  convertToOptions(selectConstants(state).ConnectorsFlows || {});

export const selectBanksFlowsOptions = (state: RootState) =>
  convertToOptions(selectConstants(state).BanksFlows || {});

export const selectBankingSdkRecipientPostalAddressTypeOptions = (state: RootState) =>
  convertToOptions(
    selectConstants(state).BankingSdkRecipientPostalAddressType || {}
  );

export const selectOnboardingApplicationStatusOptions = (state: RootState) =>
  convertToOptions(
    selectConstants(state).OnboardingApplicationStatus || {},
    status => removeUnderscoreFormatter(status)
  );

export const selectECommerceShoppingPlatforms = (state: RootState) =>
  selectConstants(state).ECommerceShoppingPlatforms ?? [];

export const selectOnboardingLegalForms = (state: RootState) =>
  selectConstants(state).OnboardingLegalForms ?? [];
